export type Quality = "low" | "medium" | "high";

export interface QualityProfile {
  quality: Quality;
  pixelRatio: number;
  shadows: boolean;
  bloom: boolean;
  smaa: boolean;
  shadowMapSize: number;
}

export function profileFor(quality: Quality): QualityProfile {
  const dpr = typeof devicePixelRatio === "number" ? devicePixelRatio : 1;
  switch (quality) {
    case "low":
      return { quality, pixelRatio: Math.min(dpr, 1), shadows: false, bloom: false, smaa: false, shadowMapSize: 512 };
    case "medium":
      return { quality, pixelRatio: Math.min(dpr, 1.5), shadows: true, bloom: true, smaa: false, shadowMapSize: 1024 };
    default:
      return { quality, pixelRatio: Math.min(dpr, 2), shadows: true, bloom: true, smaa: true, shadowMapSize: 2048 };
  }
}

/** GPU/device heuristic (R92): mobile and weak renderers start lower; the FPS governor may lower further. */
export function detectQuality(canvas?: HTMLCanvasElement): Quality {
  const touch = typeof navigator !== "undefined" && (navigator.maxTouchPoints > 1 || /Android|iPhone|iPad/i.test(navigator.userAgent));
  const cores = typeof navigator !== "undefined" ? (navigator.hardwareConcurrency ?? 4) : 4;
  let renderer = "";
  let gl: WebGLRenderingContext | WebGL2RenderingContext | null = null;
  try {
    const probe = canvas ?? document.createElement("canvas");
    gl = probe.getContext("webgl2") ?? probe.getContext("webgl");
    const info = gl?.getExtension("WEBGL_debug_renderer_info");
    if (gl && info) renderer = String(gl.getParameter(info.UNMASKED_RENDERER_WEBGL));
  } catch {
    /* ignore */
  } finally {
    // A temporary capability probe owns its context. The live scene canvas does not.
    if (!canvas) gl?.getExtension("WEBGL_lose_context")?.loseContext();
  }
  const weak = /SwiftShader|llvmpipe|Mali-4|Adreno \(TM\) [3-5]/i.test(renderer);
  if (weak) return "low";
  if (touch) return cores >= 8 ? "medium" : "low";
  return cores >= 6 ? "high" : "medium";
}
